// components/Layout/Navigation.js
import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux'; // ✅ Add Redux hooks
import { 
  Menu, 
  X, 
  Home, 
  Users, 
  BookOpen, 
  Award, 
  BarChart3, 
  Upload, 
  FileText,
  LogOut,
  GraduationCap,
  UserCheck,
  Bell,
  ClipboardList,
  TrendingUp,
  Settings,
  Database,
  Bot,
  BadgeCheck,
  Layers
} from 'lucide-react';
import * as types from '../../store/auth/login/store/_actionTypes'; // ✅ Add action types

const Navigation = ({ sidebarOpen, setSidebarOpen }) => {
  const router = useRouter();
  const dispatch = useDispatch(); // ✅ Add dispatch
  
  // ✅ Get user and auth state from Redux store
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const [navigationItems, setNavigationItems] = useState([]);

  // Define navigation items for each role
  const getNavigationForRole = (role) => {
    const normalizedRole = role?.toUpperCase(); // ✅ Normalize role case
    
    switch (normalizedRole) {
      case 'ADMIN':
        return [
          { name: 'Dashboard', href: '/admin/analytics', icon: Home },
                      { name: 'Student Management', href: '/college/student-management', icon: Users },
             {name:'Progress Tracking', href:'/admin/progress-tracking', icon: TrendingUp },
          { name: 'College Management', href: '/admin/college-management', icon: Users },
          { name: 'SME Approval', href: '/admin/sme-approval', icon: UserCheck },
          { name: 'Certificates', href: '/admin/certificates', icon: Award },
          { name: 'Student Certificates', href: '/admin/certificates/students', icon: BadgeCheck },
          // { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
          { name: 'SME Management', href: '/admin/sme-management', icon: ClipboardList },

            { name: 'Course Management', href: '/admin/course-management', icon: BookOpen },
            {name:'Admin Configuration', href:'/admin/admin-configuration', icon: Settings },
            {name:'Exam Management', href:'/admin/exams', icon: FileText },
            {name:'Question Bank', href:'/admin/question-bank', icon: Database },
            {name:'Semester Promotion', href:'/admin/semester-promotion', icon: GraduationCap },
            {name:'Chatbot Settings', href:'/admin/chatbot-settings', icon: Bot },
            {name:'Custom Cert Generator', href:'/admin/custom-certificates', icon: Layers },
           
        ];
      case 'STUDENT':
        return [
          // { name: 'Dashboard', href: '/student/dashboard', icon: Home },
          { name: 'Courses', href: '/student/courses/available_courses', icon: BookOpen },
          {name:'Notifications', href:'/student/notifications', icon: Bell },
         { name: 'Assessments', href: '/student/assessments', icon: FileText },
          { name: 'Exam', href: '/student/exam', icon: GraduationCap },
          { name: 'Results', href: '/student/results', icon: BarChart3 },
          { name:'Profile', href: '/student/profile', icon: UserCheck },
        ];
      case 'SME':
        return [
          // { name: 'Dashboard', href: '/sme/dashboard', icon: Home },
          { name: 'Content Upload', href: '/sme/content-upload', icon: Upload },
          // { name: 'Quiz Creation', href: '/sme/quiz-creation', icon: FileText },
          // { name: 'Student Analytics', href: '/sme/student-analytics', icon: TrendingUp },
          { name: 'Notifications', href: '/sme/notifications', icon: Bell },
        ];
      case 'COLLEGE':
        return [
          // { name: 'Dashboard', href: '/college/dashboard', icon: Home },
          { name: 'Student Management', href: '/college/student-management', icon: Users },
          { name: 'Progress Tracking', href: '/college/progress-tracking', icon: TrendingUp },
          { name: 'Certificate Publishing', href: '/college/certificate-publishing', icon: Award },
          { name: 'Courses', href: '/college/courses', icon: BookOpen }
        ];
      default:
        return [];
    }
  };

  // ✅ Update navigation when user or route changes
  useEffect(() => {
    console.log('Navigation - Redux user:', user);
    console.log('Navigation - Is authenticated:', isAuthenticated);
    
    if (isAuthenticated && user && user.role) {
      const items = getNavigationForRole(user.role);
      console.log('Setting navigation items for role:', user.role, items);
      setNavigationItems(items);
    } else {
      console.log('No authenticated user or role, clearing navigation');
      setNavigationItems([]);
    }
  }, [user, isAuthenticated, router.pathname]);

  // ✅ Handle logout through Redux
  const handleLogout = () => {
    console.log('Logout clicked - dispatching logout action');
    // Dispatch logout action (you may need to create this action)
  dispatch({ type: types.LOGOUT_REQUEST });
    // Or if you have a logout action:
    // dispatch(logoutRequest());
  };

  // ✅ Get user display info from Redux state
  const getUserDisplayName = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.username || user?.email || 'User';
  };

  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    const name = getUserDisplayName();
    return name.charAt(0).toUpperCase();
  };

  const getRoleDisplayName = () => {
    if (!user?.role) return 'User';
    if (user.role.toLowerCase() === 'college' && user.college?.name) {
      return user.college.name;
    }
    return user.role.toLowerCase().replace('_', ' ');
  };

  // ✅ Don't render navigation if not authenticated
  if (!isAuthenticated || !user) {
    console.log('Navigation - Not authenticated or no user, not rendering');
    return null;
  }

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b bg-blue-50">
            <div className="flex items-center space-x-2">
              <img src="/assets/logo-icon.png" alt="Gyan Sathi Logo" className="h-8 w-auto scale-150" />
              <h1 className="text-xl font-bold text-blue-900">Gyan Sathi</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md hover:bg-blue-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 bg-gray-50 border-b">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                {getUserInitials()}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{getUserDisplayName()}</p>
                <p className="text-xs text-gray-500 capitalize">{getRoleDisplayName()}</p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-4 space-y-2">
              {navigationItems.length > 0 ? (
                navigationItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = router.pathname === item.href;
                  
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-700' 
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <IconComponent className="w-5 h-5 mr-3 flex-shrink-0" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">Loading navigation...</p>
                  <p className="text-xs text-gray-400 mt-2">Role: {user?.role || 'Unknown'}</p>
                  <p className="text-xs text-gray-400">User: {user?.username || user?.email || 'None'}</p>
                </div>
              )}
            </div>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t bg-gray-50">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 hover:text-red-700 transition-colors"
            >
              <LogOut className="w-5 h-5 mr-3 flex-shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-40">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-md bg-white shadow-md hover:bg-gray-50"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>
    </>
  );
};

export default Navigation;
